import React from "react";
import { Reveal } from "./shared/Reveal";

/* Small consistent "monitored node" marker — the nervous-system motif */
const Node = ({ tone = "teal" }) => (
  <span className={`csd-node ${tone}`} aria-hidden="true" />
);

const ASSETS = [
  "Production Lines", "HVAC", "Refrigeration", "Air Compressors",
  "Motors & Pumps", "Lighting", "EV Charging", "Solar & Battery",
];

const SOURCES = [
  "Electricity", "Gas", "Water", "Temperature",
  "Humidity", "Occupancy", "Environmental",
];

const OUTPUTS = [
  {
    title: "Multi-Site Dashboard",
    body: "One head-office view across every site and circuit.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>,
  },
  {
    title: "Automated Alerts",
    body: "Abnormal-usage & fault detection, in real time.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>,
  },
  {
    title: "AI-Assisted Analysis",
    body: "Actionable intelligence — not raw data.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="6" height="6" rx="1" /><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" /></svg>,
  },
  {
    title: "Financial Reporting",
    body: "Cost & savings per site, circuit and asset.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 7c0-2-2-3-6-3S6 5 6 8s4 3 6 3 6 1 6 4-3 3-6 3-6-1-6-3" /><path d="M12 3v18" /></svg>,
  },
  {
    title: "Carbon & ESG",
    body: "Audit-ready Scope 1, 2 & 3 evidence.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6" /></svg>,
  },
  {
    title: "Measurement & Verification",
    body: "Every improvement proven, continuously.",
    icon: <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
  },
];

const Connector = ({ label }) => (
  <div className="csd-connector" aria-hidden="true">
    <span className="csd-line"><span className="csd-flow" /></span>
    {label && <span className="csd-conlabel">{label}</span>}
  </div>
);

export default function CIISystemDiagram() {
  return (
    <Reveal className="csd" tag="div" data-testid="cii-system-diagram">
      {/* Layer 1 — the estate */}
      <div className="csd-layer">
        <div className="csd-eyebrow teal">YOUR ESTATE · EVERY ASSET, EQUIPMENT &amp; CIRCUIT</div>
        <div className="csd-chips">
          {ASSETS.map((a) => (
            <div className="csd-chip" key={a}><Node tone="teal" />{a}</div>
          ))}
        </div>
      </div>

      <Connector label="Measured individually — nothing assumed" />

      {/* Layer 2 — measured at source */}
      <div className="csd-layer">
        <div className="csd-eyebrow teal">MEASURED AT SOURCE · UTILITIES &amp; SENSORS</div>
        <div className="csd-chips">
          {SOURCES.map((s) => (
            <div className="csd-chip" key={s}><Node tone="gold" />{s}</div>
          ))}
        </div>
      </div>

      <Connector label="Captured minute-by-minute" />

      {/* Layer 3 — collection + secure comms */}
      <div className="csd-layer">
        <div className="csd-band" data-testid="csd-secure">
          <span className="csd-band-ic">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          </span>
          <div>
            <div className="csd-band-t">Local Data Collection &amp; Secure Communication</div>
            <div className="csd-band-s">Eniscope metering gathers every reading on-site and transmits it encrypted &amp; secure.</div>
          </div>
        </div>
      </div>

      <Connector label="Every data point converges" />

      {/* Layer 4 — the brain */}
      <div className="csd-brainwrap">
        <div className="csd-brain" data-testid="csd-brain">
          <div className="csd-brain-tag">CII&trade;</div>
          <div className="csd-brain-t">Centralised Intelligence Infrastructure</div>
          <div className="csd-brain-s">The Digital Nervous System — Making the Invisible Visible</div>
        </div>
        <div className="csd-diverge" aria-hidden="true" />
      </div>

      <div className="csd-outlabel">INTELLIGENCE DELIVERED</div>

      {/* Layer 5 — outputs */}
      <div className="csd-outputs">
        {OUTPUTS.map((o, i) => (
          <div className="csd-out" key={o.title} style={{ transitionDelay: `${(i % 3) * 0.06}s` }} data-testid={`csd-output-${i}`}>
            <div className="csd-out-ic">{o.icon}</div>
            <div className="csd-out-t">{o.title}</div>
            <p>{o.body}</p>
          </div>
        ))}
      </div>
    </Reveal>
  );
}
